/* Durnev — dual-handle price slider.
   Wraps two overlapping <input type="range"> elements and two number
   inputs that hold the actual `filter.v.price.gte`/`lte` values consumed
   by Shopify facets. The slider is purely a UX layer: it mirrors the
   number inputs and forwards user changes to them so facets.js auto-
   submits the form on change. */
(function () {
  if (customElements.get('durnev-price-slider')) return;

  class DurnevPriceSlider extends HTMLElement {
    connectedCallback() {
      this.lo = this.querySelector('.durnev-coll-filters__price-range--lo');
      this.hi = this.querySelector('.durnev-coll-filters__price-range--hi');
      const numberInputs = this.querySelectorAll('input[type="number"]');
      this.numLo = numberInputs[0];
      this.numHi = numberInputs[1];

      if (!this.lo || !this.hi || !this.numLo || !this.numHi) return;

      this.min = parseFloat(this.dataset.min) || 0;
      this.max = parseFloat(this.dataset.max) || 0;
      if (this.max <= this.min) this.max = this.min + 1;

      // Seed range values from existing number inputs (active filter state).
      const initialLo = this.numLo.value !== '' ? parseFloat(this.numLo.value) : this.min;
      const initialHi = this.numHi.value !== '' ? parseFloat(this.numHi.value) : this.max;
      this.lo.value = this.clamp(initialLo);
      this.hi.value = this.clamp(initialHi);

      this.lo.addEventListener('input', () => this.onRange('lo'));
      this.hi.addEventListener('input', () => this.onRange('hi'));
      this.lo.addEventListener('change', () => this.commit('lo'));
      this.hi.addEventListener('change', () => this.commit('hi'));
      this.numLo.addEventListener('input', () => this.onNumber());
      this.numHi.addEventListener('input', () => this.onNumber());

      this.paint();
    }

    clamp(v) {
      if (Number.isNaN(v)) return this.min;
      if (v < this.min) return this.min;
      if (v > this.max) return this.max;
      return v;
    }

    onRange(which) {
      let lo = parseFloat(this.lo.value);
      let hi = parseFloat(this.hi.value);
      if (which === 'lo' && lo > hi) { lo = hi; this.lo.value = lo; }
      if (which === 'hi' && hi < lo) { hi = lo; this.hi.value = hi; }
      // Mirror visual state instantly; defer number-input commit to `change`
      // so we don't fire a Shopify request on every dragged pixel.
      this.paint();
    }

    commit(which) {
      const lo = parseFloat(this.lo.value);
      const hi = parseFloat(this.hi.value);
      // Only write a number-input value when it differs from the silent
      // default (full range), so the URL stays clean if the user resets.
      this.writeNumber(this.numLo, lo, this.min);
      this.writeNumber(this.numHi, hi, this.max);
    }

    writeNumber(input, val, sentinel) {
      const next = (val === sentinel) ? '' : String(Math.round(val));
      if (input.value === next) return;
      input.value = next;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onNumber() {
      const lo = this.numLo.value !== '' ? parseFloat(this.numLo.value) : this.min;
      const hi = this.numHi.value !== '' ? parseFloat(this.numHi.value) : this.max;
      this.lo.value = this.clamp(Math.min(lo, hi));
      this.hi.value = this.clamp(Math.max(lo, hi));
      this.paint();
    }

    paint() {
      const range = this.max - this.min || 1;
      const lo = parseFloat(this.lo.value);
      const hi = parseFloat(this.hi.value);
      const left = ((lo - this.min) / range) * 100;
      const right = ((hi - this.min) / range) * 100;
      // CSS uses calc(10px + (100% - 20px) * var(--lo)/100) so the fill
      // aligns with the centers of the 20px thumbs, not the wrapper edges.
      this.style.setProperty('--lo', left);
      this.style.setProperty('--hi', right);
    }
  }

  customElements.define('durnev-price-slider', DurnevPriceSlider);
})();
